import type { SvgIconComponent } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DownloadIcon from '@mui/icons-material/Download';
import EditNoteIcon from '@mui/icons-material/EditNote';
import HandshakeIcon from '@mui/icons-material/Handshake';
import InsightsIcon from '@mui/icons-material/Insights';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip as MuiTooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { bloom, sage } from '../../shared/ui/theme/colors';
import {
  type ArtifactDashboardRow,
  useGetArtifactDashboardQuery,
} from './api/artifactsDashboardApi';
import { downloadAdminCsv } from './utils/downloadAdminCsv';

// ─── Types ────────────────────────────────────────────────────────────────────

type ArtifactRow = ArtifactDashboardRow;

interface BreakdownItem {
  key: string;
  label: string;
  sublabel?: string;
  count: number;
  share: number; // 0..1
  icon?: ReactNode; // optional leading icon in the label cell
  color?: string; // optional override for the distribution bar
}

interface VolumePoint {
  bucket: string; // YYYY-MM-DD (start of bucket)
  count: number;
}

// ─── Artifact metadata ────────────────────────────────────────────────────────

// Audience-based palette. Coach-authored artifact types use the brand
// sage palette (`success.main`); member-authored ones use bloom (i.e.
// `info.main`, the purple in our MUI theme). We hold the literal hexes
// here so we can drive raw SVG strokes/fills (recharts, Chip outlines)
// without piping through the theme provider.
type Audience = 'coach' | 'member';

const AUDIENCE_COLOR: Record<Audience, { main: string; dark: string; tonal: string }> = {
  coach: { main: sage[500], dark: sage[600], tonal: sage[200] },
  member: { main: bloom[500], dark: bloom[600], tonal: bloom[200] },
};

// Per-slug fill colors for the area chart and tables. Multiple slugs
// share an audience family, so to keep series visually distinct we
// walk maximally wide HSL-lightness ramps within each family. Steps
// are ~15 lightness apart, which is well above the JND for HSL
// lightness — even the middle shades read clearly different. Brand
// midtones (sage[500], bloom[500]) intentionally don't anchor the
// ramp; they sit too low-saturation to act as legible series colors.
const COACH_PALETTE = [
  '#0F2418', // very dark forest
  '#244B33',
  '#43764F',
  '#6CA178',
  '#9CC4A4',
  '#CCE3D0',
  '#EAF3EC', // very pale mint
];
const MEMBER_PALETTE = [
  '#1F0A22', // very dark plum
  '#4B1F4D',
  '#7A3878',
  '#A858A2',
  '#CB89C5',
  '#E5BCE0',
  '#F5E2F2', // very pale orchid
];

interface ProfileMeta {
  title: string;
  audience: Audience;
  Icon: SvgIconComponent;
}

// Human-readable artifact-type metadata, keyed by internal profile slug.
// The dashboard surfaces titles + icons + colors everywhere user-facing;
// the slug stays as the stable identifier so a future backend swap is
// straightforward.
const PROFILE_META: Record<string, ProfileMeta> = {
  'coaching-conversation-summary': {
    title: 'Coach - Session Summary',
    audience: 'coach',
    Icon: SummarizeIcon,
  },
  'coaching-conversations-summary': {
    title: 'Coaching Arc',
    audience: 'coach',
    Icon: TimelineIcon,
  },
  'coaching-feedback': {
    title: 'Coaching Reflection',
    audience: 'coach',
    Icon: PsychologyIcon,
  },
  'coaching-feedback-arc': {
    title: 'Coaching Reflection Arc',
    audience: 'coach',
    Icon: AutoStoriesIcon,
  },
  'session-prep-coach': {
    title: 'Coach - Session Prep',
    audience: 'coach',
    Icon: EditNoteIcon,
  },
  'chem-call-reflection': {
    title: 'Coach - Chem Call Reflection',
    audience: 'coach',
    Icon: HandshakeIcon,
  },
  'member-reflection': {
    title: 'Member Reflection',
    audience: 'member',
    Icon: SelfImprovementIcon,
  },
  'chem-call-reflection-member': {
    title: 'Member - Chemistry Call Reflection',
    audience: 'member',
    Icon: HandshakeIcon,
  },
  'member-reflection-arc': {
    title: 'Member Reflection Arc',
    audience: 'member',
    Icon: InsightsIcon,
  },
  'session-prep-member': {
    title: 'Member - Session Prep',
    audience: 'member',
    Icon: AssignmentIcon,
  },
};

const PROFILE_SLUGS = Object.keys(PROFILE_META);

function profileMeta(slug: string): ProfileMeta {
  return (
    PROFILE_META[slug] || {
      title: slug,
      audience: 'coach',
      Icon: SummarizeIcon,
    }
  );
}

function profileTitle(slug: string): string {
  return profileMeta(slug).title;
}

// Stable color per slug for the pie chart. Computed lazily on first use
// based on order within the audience's palette. Cached so multiple
// renders return the same hue.
const PROFILE_PIE_COLOR: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  let coachIdx = 0;
  let memberIdx = 0;
  for (const slug of PROFILE_SLUGS) {
    const meta = profileMeta(slug);
    if (meta.audience === 'coach') {
      out[slug] = COACH_PALETTE[coachIdx % COACH_PALETTE.length];
      coachIdx += 1;
    } else {
      out[slug] = MEMBER_PALETTE[memberIdx % MEMBER_PALETTE.length];
      memberIdx += 1;
    }
  }
  return out;
})();
// ─── Date range presets ──────────────────────────────────────────────────────

type DateRangePreset = '7d' | '30d' | '2m' | '6m' | '12m' | 'all';

const DATE_PRESETS: readonly { key: DateRangePreset; label: string }[] = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '2m', label: 'Last 2 months' },
  { key: '6m', label: 'Last 6 months' },
  { key: '12m', label: 'Last 12 months' },
  { key: 'all', label: 'All time' },
] as const;

const DEFAULT_DATE_PRESET: DateRangePreset = '30d';

function presetFromDate(preset: DateRangePreset): string {
  if (preset === 'all') return '';
  const now = new Date();
  let target: Date;
  if (preset === '7d') {
    target = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
  } else if (preset === '30d') {
    target = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
  } else {
    const months = preset === '2m' ? 2 : preset === '6m' ? 6 : 12;
    // Avoid Date.setMonth() day-overflow: clamp the day to one that
    // exists in the target month (same fix used on the transcript page).
    const targetYearMonth = new Date(now.getFullYear(), now.getMonth() - months, 1);
    const lastDayOfTargetMonth = new Date(
      targetYearMonth.getFullYear(),
      targetYearMonth.getMonth() + 1,
      0
    ).getDate();
    target = new Date(
      targetYearMonth.getFullYear(),
      targetYearMonth.getMonth(),
      Math.min(now.getDate(), lastDayOfTargetMonth)
    );
  }
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ─── Aggregation helpers (pure) ──────────────────────────────────────────────

export function filterByDate(rows: ArtifactRow[], dateFrom: string, dateTo: string): ArtifactRow[] {
  if (!dateFrom && !dateTo) return rows;
  return rows.filter((r) => {
    const day = r.created_at.slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  });
}

function isoWeekStart(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const dow = d.getDay(); // 0..6, Sun..Sat
  const diff = (dow + 6) % 7; // make Monday the week start
  d.setDate(d.getDate() - diff);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function volumeOverTime(rows: ArtifactRow[]): VolumePoint[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const day = r.created_at.slice(0, 10);
    const key = isoWeekStart(day);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([bucket, count]) => ({ bucket, count }));
}

// ─── Coach/org stats aggregation ───────────────────────────────────────────────
//
// Both "By coach" and "By org" tables show the same column set
// (Members / Sessions / Artifacts / Artifacts-per-session + top-5
// type distribution), so they share a single row shape and a single
// table component.

export interface EntityStat {
  id: string;
  name: string;
  sublabel?: string; // e.g. coach email, or "N coaches" for an org
  artifacts: number;
  members: number;
  sessions: number;
  perSession: number; // artifacts / sessions, 0 when no sessions
  artifactsByType: Record<string, number>; // slug → count for this entity
}

export type CoachStat = EntityStat; // kept for clarity at call sites
export type OrgStat = EntityStat;

// Aggregate artifact rows by entity key, then attach static per-entity
// stats (members, sessions). Entities with zero artifacts in range are
// omitted — the dashboard is about activity.
function aggregateByEntity(
  rows: ArtifactRow[],
  keyOf: (r: ArtifactRow) => string,
  meta: Map<string, { name: string; sublabel?: string; members: number; sessions: number }>
): EntityStat[] {
  const typeCounts = new Map<string, Map<string, number>>();
  const artifactCounts = new Map<string, number>();
  for (const r of rows) {
    const k = keyOf(r);
    artifactCounts.set(k, (artifactCounts.get(k) || 0) + 1);
    let tc = typeCounts.get(k);
    if (!tc) {
      tc = new Map();
      typeCounts.set(k, tc);
    }
    tc.set(r.profile_slug, (tc.get(r.profile_slug) || 0) + 1);
  }
  const out: EntityStat[] = [];
  for (const [key, artifacts] of artifactCounts.entries()) {
    const m = meta.get(key);
    if (!m) continue;
    const tc = typeCounts.get(key) || new Map<string, number>();
    const artifactsByType: Record<string, number> = {};
    for (const [slug, count] of tc.entries()) {
      artifactsByType[slug] = count;
    }
    out.push({
      id: key,
      name: m.name,
      sublabel: m.sublabel,
      artifacts,
      members: m.members,
      sessions: m.sessions,
      perSession: m.sessions > 0 ? artifacts / m.sessions : 0,
      artifactsByType,
    });
  }
  return out;
}

export function coachStats(
  rows: ArtifactRow[],
  coaches: {
    id: string;
    name: string;
    email: string;
    members_count: number;
    sessions_count: number;
  }[]
): CoachStat[] {
  const meta = new Map(
    coaches.map((c) => [
      c.id,
      { name: c.name, sublabel: c.email, members: c.members_count, sessions: c.sessions_count },
    ])
  );
  return aggregateByEntity(rows, (r) => r.owner_person_id, meta);
}

// Org-level aggregation rolls up the members & sessions of every coach
// that belongs to the org. (Note: members are not deduped across
// coaches — in real data, two coaches at the same org coaching the same
// person is rare enough that we can refine later when wiring real data.)
export function orgStats(
  rows: ArtifactRow[],
  coaches: {
    id: string;
    org_id: string;
    org_name: string;
    members_count: number;
    sessions_count: number;
  }[]
): OrgStat[] {
  const orgMeta = new Map<
    string,
    { name: string; sublabel?: string; members: number; sessions: number; coachCount: number }
  >();
  for (const c of coaches) {
    const existing = orgMeta.get(c.org_id);
    if (existing) {
      existing.members += c.members_count;
      existing.sessions += c.sessions_count;
      existing.coachCount += 1;
    } else {
      orgMeta.set(c.org_id, {
        name: c.org_name,
        members: c.members_count,
        sessions: c.sessions_count,
        coachCount: 1,
      });
    }
  }
  // Format sublabel after the rollup (depends on final coachCount).
  for (const v of orgMeta.values()) {
    v.sublabel = `${v.coachCount} coach${v.coachCount === 1 ? '' : 'es'}`;
  }
  return aggregateByEntity(rows, (r) => r.org_id, orgMeta);
}

type LabelMeta = { label: string; sublabel?: string; icon?: ReactNode; color?: string };

export function breakdownBy(
  rows: ArtifactRow[],
  keyOf: (r: ArtifactRow) => string,
  labelOf: (r: ArtifactRow) => LabelMeta
): BreakdownItem[] {
  const counts = new Map<string, { count: number } & LabelMeta>();
  for (const r of rows) {
    const k = keyOf(r);
    const existing = counts.get(k);
    if (existing) existing.count += 1;
    else counts.set(k, { count: 1, ...labelOf(r) });
  }
  const total = rows.length || 1;
  return [...counts.entries()].map(([key, v]) => ({
    key,
    label: v.label,
    sublabel: v.sublabel,
    icon: v.icon,
    color: v.color,
    count: v.count,
    share: v.count / total,
  }));
}

// ─── CSV export ──────────────────────────────────────────────────────────────
// Exported so the /admin card and dashboard button use the same backend
// download target and cannot drift on columns or filename behavior.
export async function downloadArtifactsCsv(): Promise<void> {
  await downloadAdminCsv({ target: 'admin.artifacts.generated_csv' });
}
// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 160, flex: '1 1 160px' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5">{value}</Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// Shared row table used by both "By coach" and "By org" sections — same
// column set (members / sessions / artifacts / artifacts-per-session +
// one column per artifact type), differing only in the first column
// header.
type EntitySortKey = 'name' | 'artifacts' | 'members' | 'sessions' | 'perSession';

// Tighter padding on numeric columns: the default MUI `size="small"`
// cell padding still leaves a lot of empty space between short numbers,
// which made the table feel sparse and pushed the top-5 column into a
// scroll. We pull it in symmetrically.
const NUM_CELL_SX = { px: 1, width: 72 } as const;

function EntityTable({
  title,
  entityHeader,
  data,
}: {
  title: string;
  entityHeader: string;
  data: EntityStat[];
}) {
  const [sortKey, setSortKey] = useState<EntitySortKey>('artifacts');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');

  function toggleSort(key: EntitySortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }

  const processed = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? data.filter(
          (d) => d.name.toLowerCase().includes(q) || (d.sublabel || '').toLowerCase().includes(q)
        )
      : data;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'artifacts':
          cmp = a.artifacts - b.artifacts;
          break;
        case 'members':
          cmp = a.members - b.members;
          break;
        case 'sessions':
          cmp = a.sessions - b.sessions;
          break;
        case 'perSession':
          cmp = a.perSession - b.perSession;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, search, sortKey, sortDir]);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">{title}</Typography>
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
        />
      </Box>
      <TableContainer>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortKey === 'name' ? sortDir : false} sx={{ width: 200 }}>
                <TableSortLabel
                  active={sortKey === 'name'}
                  direction={sortKey === 'name' ? sortDir : 'asc'}
                  onClick={() => toggleSort('name')}
                >
                  {entityHeader}
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortKey === 'members' ? sortDir : false}
                sx={NUM_CELL_SX}
              >
                <TableSortLabel
                  active={sortKey === 'members'}
                  direction={sortKey === 'members' ? sortDir : 'desc'}
                  onClick={() => toggleSort('members')}
                >
                  Members
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortKey === 'sessions' ? sortDir : false}
                sx={NUM_CELL_SX}
              >
                <TableSortLabel
                  active={sortKey === 'sessions'}
                  direction={sortKey === 'sessions' ? sortDir : 'desc'}
                  onClick={() => toggleSort('sessions')}
                >
                  Sessions
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortKey === 'artifacts' ? sortDir : false}
                sx={NUM_CELL_SX}
              >
                <TableSortLabel
                  active={sortKey === 'artifacts'}
                  direction={sortKey === 'artifacts' ? sortDir : 'desc'}
                  onClick={() => toggleSort('artifacts')}
                >
                  Artifacts
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortKey === 'perSession' ? sortDir : false}
                sx={{ ...NUM_CELL_SX, width: 96 }}
              >
                <TableSortLabel
                  active={sortKey === 'perSession'}
                  direction={sortKey === 'perSession' ? sortDir : 'desc'}
                  onClick={() => toggleSort('perSession')}
                >
                  Artifacts / session
                </TableSortLabel>
              </TableCell>
              {PROFILE_SLUGS.map((slug) => {
                const meta = profileMeta(slug);
                const color = PROFILE_PIE_COLOR[slug];
                const Icon = meta.Icon;
                return (
                  <TableCell key={slug} align="center" sx={{ ...NUM_CELL_SX, width: 52 }}>
                    <MuiTooltip title={meta.title} arrow>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color,
                        }}
                      >
                        <Icon style={{ fontSize: 18 }} />
                      </Box>
                    </MuiTooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {processed.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell sx={{ width: 200 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={row.name}
                    >
                      {row.name}
                    </Typography>
                    {row.sublabel && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={row.sublabel}
                      >
                        {row.sublabel}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={NUM_CELL_SX}>
                  {row.members.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={NUM_CELL_SX}>
                  {row.sessions.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={NUM_CELL_SX}>
                  {row.artifacts.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ ...NUM_CELL_SX, width: 96 }}>
                  {row.perSession.toFixed(1)}
                </TableCell>
                {PROFILE_SLUGS.map((slug) => {
                  const n = row.artifactsByType[slug] || 0;
                  return (
                    <TableCell
                      key={slug}
                      align="center"
                      sx={{
                        ...NUM_CELL_SX,
                        width: 52,
                        color: n === 0 ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {n.toLocaleString()}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {processed.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={5 + PROFILE_SLUGS.length}
                  align="center"
                  sx={{ color: 'text.secondary' }}
                >
                  No matches
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── By-artifact-type breakdown ────────────────────────────────────────────────
//
// Composed of two views over the same filtered dataset:
//   1. "Adoption over time" — stacked area chart, one band per
//      artifact type, weekly buckets. Shows ramp + composition shifts.
//   2. Sortable data table — totals, unique coaches, unique members,
//      plus a per-row volume bar so the relative size of each type is
//      visible at a glance without a redundant separate chart.
//
// (Replaces the previous pie chart, which hid totals — the actual
// useful number for adoption tracking.)

// Compact sortable header cell used by the artifact-type legend. Plain
// MUI TableSortLabel relies on table context, so we render an in-line
// equivalent that works inside our CSS grid.
function SortableHeader({
  label,
  sortKey,
  activeKey,
  direction,
  onClick,
  defaultDir,
  align = 'left',
}: {
  label: string;
  sortKey: ArtifactTypeSortKey;
  activeKey: ArtifactTypeSortKey;
  direction: 'asc' | 'desc';
  onClick: (key: ArtifactTypeSortKey) => void;
  defaultDir: 'asc' | 'desc';
  align?: 'left' | 'right';
}) {
  const isActive = activeKey === sortKey;
  const dir = isActive ? direction : defaultDir;
  const arrow = dir === 'asc' ? ' ↑' : ' ↓';
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick(sortKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(sortKey);
        }
      }}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align,
        color: isActive ? 'text.primary' : 'text.secondary',
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.66,
        outline: 'none',
        '&:hover': { color: 'text.primary' },
        '&:focus-visible': { textDecoration: 'underline' },
      }}
    >
      {label}
      <Box component="span" sx={{ opacity: isActive ? 1 : 0.3 }}>
        {arrow}
      </Box>
    </Box>
  );
}

type ArtifactTypeSortKey = 'name' | 'count' | 'uniqueCoaches' | 'uniqueMembers';

function ArtifactTypeBreakdown({ rows }: { rows: ArtifactRow[] }) {
  // Sort state controls the legend/data-table order only. The stacked
  // area chart keeps its audience-grouped order so the green and purple
  // bands stay visually contiguous regardless of how the table is
  // sorted.
  const [sortKey, setSortKey] = useState<ArtifactTypeSortKey>('count');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  function toggleSort(key: ArtifactTypeSortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  }
  const data = useMemo(() => {
    // Per-slug rollups: count, distinct coaches, distinct members.
    const counts = new Map<string, { count: number; coaches: Set<string>; members: Set<string> }>();
    for (const r of rows) {
      let agg = counts.get(r.profile_slug);
      if (!agg) {
        agg = { count: 0, coaches: new Set(), members: new Set() };
        counts.set(r.profile_slug, agg);
      }
      agg.count += 1;
      agg.coaches.add(r.owner_person_id);
      agg.members.add(r.member_person_id);
    }
    const total = rows.length || 1;
    return (
      [...counts.entries()]
        .map(([slug, agg]) => {
          const meta = profileMeta(slug);
          return {
            slug,
            name: meta.title,
            audience: meta.audience,
            Icon: meta.Icon,
            color: PROFILE_PIE_COLOR[slug] || AUDIENCE_COLOR[meta.audience].main,
            count: agg.count,
            uniqueCoaches: agg.coaches.size,
            uniqueMembers: agg.members.size,
            share: agg.count / total,
          };
        })
        // Group by audience (coach slices contiguous, then member slices),
        // ordered by count within each group. Recharts walks the data
        // array in order around the pie, so this guarantees the green
        // wedges sit next to each other and the purple wedges next to each
        // other — no zebra striping.
        .sort((a, b) => {
          if (a.audience !== b.audience) return a.audience === 'coach' ? -1 : 1;
          return b.count - a.count;
        })
    );
  }, [rows]);

  const legendData = useMemo(() => {
    return [...data].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'count':
          cmp = a.count - b.count;
          break;
        case 'uniqueCoaches':
          cmp = a.uniqueCoaches - b.uniqueCoaches;
          break;
        case 'uniqueMembers':
          cmp = a.uniqueMembers - b.uniqueMembers;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  // Slugs the user has toggled off in the area-chart legend. Hidden
  // slugs still render as cells (hide={true}) so recharts keeps stack
  // math consistent — they just visually disappear.
  const [hiddenSlugs, setHiddenSlugs] = useState<ReadonlySet<string>>(() => new Set());
  function toggleSlugVisibility(slug: string) {
    setHiddenSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  // Audience-grouped slug order, used both to set Z-order in the
  // stacked area chart and to drive consistent series-color mapping.
  const stackedSlugs = useMemo(
    () =>
      [...data].sort((a, b) => {
        if (a.audience !== b.audience) return a.audience === 'coach' ? -1 : 1;
        return b.count - a.count;
      }),
    [data]
  );

  // Per-week series shape recharts wants:
  //   [{ week: '2025-12-29', '<slug-a>': 12, '<slug-b>': 4, ... }, ...]
  const adoptionSeries = useMemo(() => {
    if (rows.length === 0) return [] as Array<Record<string, number | string>>;
    const byWeek = new Map<string, Map<string, number>>();
    for (const r of rows) {
      const wk = isoWeekStart(r.created_at.slice(0, 10));
      let perSlug = byWeek.get(wk);
      if (!perSlug) {
        perSlug = new Map();
        byWeek.set(wk, perSlug);
      }
      perSlug.set(r.profile_slug, (perSlug.get(r.profile_slug) || 0) + 1);
    }
    const slugs = data.map((d) => d.slug);
    return [...byWeek.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([week, perSlug]) => {
        const point: Record<string, number | string> = { week };
        for (const slug of slugs) {
          point[slug] = perSlug.get(slug) || 0;
        }
        return point;
      });
  }, [rows, data]);

  const maxCount = Math.max(1, ...data.map((d) => d.count));

  if (data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No artifacts in range.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Adoption over time — stacked area chart */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Adoption over time
        </Typography>
        <Box sx={{ width: '100%', height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={adoptionSeries} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" minTickGap={32} />
              <YAxis allowDecimals={false} />
              <Tooltip
                formatter={(value, name) => {
                  const numericValue = typeof value === 'number' ? value : Number(value) || 0;
                  const title = typeof name === 'string' ? profileTitle(name) : String(name);
                  return [numericValue.toLocaleString(), title];
                }}
              />
              {stackedSlugs.map((d) => (
                <Area
                  key={d.slug}
                  type="monotone"
                  dataKey={d.slug}
                  name={d.slug}
                  stackId="types"
                  stroke={d.color}
                  fill={d.color}
                  fillOpacity={0.85}
                  isAnimationActive={false}
                  hide={hiddenSlugs.has(d.slug)}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Box>
        {/* Interactive legend — click to toggle a series in the chart. */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          {stackedSlugs.map((d) => {
            const hidden = hiddenSlugs.has(d.slug);
            const Icon = d.Icon;
            return (
              <Box
                key={d.slug}
                role="button"
                tabIndex={0}
                onClick={() => toggleSlugVisibility(d.slug)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSlugVisibility(d.slug);
                  }
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: 1,
                  borderColor: hidden ? 'divider' : d.color,
                  bgcolor: hidden ? 'transparent' : `${d.color}22`,
                  color: hidden ? 'text.disabled' : 'text.primary',
                  opacity: hidden ? 0.55 : 1,
                  textDecoration: hidden ? 'line-through' : 'none',
                  outline: 'none',
                  '&:hover': { bgcolor: hidden ? 'action.hover' : `${d.color}33` },
                  '&:focus-visible': { boxShadow: '0 0 0 2px rgba(25,118,210,0.4)' },
                }}
                title={hidden ? `Show ${d.name}` : `Hide ${d.name}`}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: 0.5,
                    bgcolor: hidden ? 'transparent' : d.color,
                    border: hidden ? `1px solid ${d.color}` : 'none',
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: hidden ? 'text.disabled' : d.color,
                  }}
                >
                  <Icon style={{ fontSize: 14 }} />
                </Box>
                <Typography variant="caption">{d.name}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* Sortable data table with embedded volume bar */}
      <Box
        sx={{
          display: 'grid',
          // swatch | icon | title | total artifacts | unique coaches | unique members | volume bar
          gridTemplateColumns: 'auto auto 1fr auto auto auto 1.2fr',
          rowGap: 0.75,
          columnGap: 2,
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Header row */}
        <Box sx={{ display: 'contents' }}>
          <Box />
          <Box />
          <SortableHeader
            label="Title"
            sortKey="name"
            activeKey={sortKey}
            direction={sortDir}
            onClick={toggleSort}
            defaultDir="asc"
          />
          <SortableHeader
            label="Total artifacts"
            sortKey="count"
            activeKey={sortKey}
            direction={sortDir}
            onClick={toggleSort}
            defaultDir="desc"
            align="right"
          />
          <SortableHeader
            label="Unique coaches"
            sortKey="uniqueCoaches"
            activeKey={sortKey}
            direction={sortDir}
            onClick={toggleSort}
            defaultDir="desc"
            align="right"
          />
          <SortableHeader
            label="Unique members"
            sortKey="uniqueMembers"
            activeKey={sortKey}
            direction={sortDir}
            onClick={toggleSort}
            defaultDir="desc"
            align="right"
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Volume
          </Typography>
        </Box>
        {/* Divider line spanning all columns */}
        <Box
          sx={{
            gridColumn: '1 / -1',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        />
        {legendData.map((d) => {
          const Icon = d.Icon;
          return (
            <Box key={d.slug} sx={{ display: 'contents' }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: 0.5,
                  bgcolor: d.color,
                }}
              />
              <Box sx={{ display: 'inline-flex', alignItems: 'center', color: d.color }}>
                <Icon style={{ fontSize: 16 }} />
              </Box>
              <Typography variant="body2" noWrap title={d.name}>
                {d.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                {d.count.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                {d.uniqueCoaches.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                {d.uniqueMembers.toLocaleString()}
              </Typography>
              {/* Per-row volume bar */}
              <Box
                sx={{
                  height: 8,
                  borderRadius: 0.5,
                  bgcolor: 'action.hover',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${(d.count / maxCount) * 100}%`,
                    bgcolor: d.color,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────

export default function ArtifactsDashboardPage() {
  const { data, isLoading, error, refetch } = useGetArtifactDashboardQuery();
  const rows = data?.artifacts ?? [];
  const coaches = data?.coaches ?? [];

  const [activePreset, setActivePreset] = useState<DateRangePreset | null>(DEFAULT_DATE_PRESET);
  const [dateFrom, setDateFrom] = useState<string>(() => presetFromDate(DEFAULT_DATE_PRESET));
  const [dateTo, setDateTo] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string | null>(null);

  function applyDatePreset(p: DateRangePreset) {
    setActivePreset(p);
    setDateFrom(presetFromDate(p));
    setDateTo('');
  }

  const filtered = useMemo(() => filterByDate(rows, dateFrom, dateTo), [rows, dateFrom, dateTo]);

  const volume = useMemo(() => volumeOverTime(filtered), [filtered]);

  const coachRows = useMemo(() => coachStats(filtered, coaches), [filtered, coaches]);
  const orgRows = useMemo(() => orgStats(filtered, coaches), [filtered, coaches]);

  const distinctCoaches = coachRows.length;
  const distinctOrgs = orgRows.length;
  const distinctProfiles = useMemo(
    () => new Set(filtered.map((r) => r.profile_slug)).size,
    [filtered]
  );

  async function handleDownloadCsv() {
    setDownloadError(null);
    try {
      await downloadArtifactsCsv();
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1280, mx: 'auto', p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load artifact dashboard data.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Artifacts generated
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Volume and breakdowns for artifacts produced across the platform.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadCsv}
          sx={{ flexShrink: 0 }}
        >
          Download CSV
        </Button>
      </Box>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {downloadError}
        </Alert>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Range:
          </Typography>
          {DATE_PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              size="small"
              variant={activePreset === p.key ? 'filled' : 'outlined'}
              color={activePreset === p.key ? 'primary' : 'default'}
              onClick={() => applyDatePreset(p.key)}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setActivePreset(null);
            }}
            sx={{ width: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setActivePreset(null);
            }}
            sx={{ width: 160 }}
          />
          {activePreset !== DEFAULT_DATE_PRESET && (
            <Button
              size="small"
              onClick={() => {
                setActivePreset(DEFAULT_DATE_PRESET);
                setDateFrom(presetFromDate(DEFAULT_DATE_PRESET));
                setDateTo('');
              }}
            >
              Reset
            </Button>
          )}
        </Box>
      </Paper>

      {/* KPI strip */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <KpiCard label="Artifacts" value={filtered.length.toLocaleString()} sub="in range" />
        <KpiCard label="Distinct coaches" value={distinctCoaches.toLocaleString()} />
        <KpiCard label="Distinct orgs" value={distinctOrgs.toLocaleString()} />
        <KpiCard label="Distinct artifact types" value={distinctProfiles.toLocaleString()} />
      </Box>

      {/* Volume over time */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Volume over time</Typography>
          <Typography variant="caption" color="text.secondary">
            Weekly buckets (Mon-start)
          </Typography>
        </Box>
        <Box sx={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={volume} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" minTickGap={32} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#1976d2"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* By artifact type — pie chart with hoverable shares */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h6">By artifact type</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Legend:
            </Typography>
            <Chip
              size="small"
              label="Coach artifact"
              sx={{
                height: 22,
                borderColor: AUDIENCE_COLOR.coach.main,
                color: AUDIENCE_COLOR.coach.dark,
                bgcolor: AUDIENCE_COLOR.coach.tonal,
              }}
              variant="outlined"
            />
            <Chip
              size="small"
              label="Member artifact"
              sx={{
                height: 22,
                borderColor: AUDIENCE_COLOR.member.main,
                color: AUDIENCE_COLOR.member.dark,
                bgcolor: AUDIENCE_COLOR.member.tonal,
              }}
              variant="outlined"
            />
          </Box>
        </Box>
        <ArtifactTypeBreakdown rows={filtered} />
      </Paper>
      <EntityTable title="By coach" entityHeader="Coach" data={coachRows} />
      <EntityTable title="By org" entityHeader="Org" data={orgRows} />
    </Box>
  );
}
